import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useEffect } from 'react'

import ErrorBoundary from '@/components/ErrorBoundary'
import { FullPageLoader } from '@/components/Loader'

import { PlatformAuthProvider, usePlatformAuth } from './auth'
import PlatformLayout from './PlatformLayout'
import PlatformLoginPage from './LoginPage'
import OrganizationsPage from './OrganizationsPage'
import OrganizationPage from './OrganizationPage'
import AdminsPage from './AdminsPage'
import AuditPage from './AuditPage'
import PlatformPasswordPage from './PasswordPage'

import '@/styles/platform.css'

/**
 * The admin portal, mounted at /admin and nowhere else.
 *
 * It is not the application: its own session, its own principal, and a carrier
 * user who signs in cannot reach it. The carrier's `AuthProvider` is not in the
 * tree above this - `main.tsx` chooses one or the other - so nothing here can
 * read a carrier session or call `/auth/me`.
 */

/**
 * `/admin` on a carrier's host is not a page - it is a wrong turn.
 *
 * The API refuses every `/platform` path there with 404, so a sign-in form
 * rendered on `acme.insuronix.ai/admin/login` would take a password, send it
 * nowhere, and report a failure that says nothing about what is wrong. Sending
 * the person to that carrier's own sign-in page is the honest answer, and it
 * has to be a real navigation rather than a route change: the carrier
 * application is not mounted here, so only a page load can reach it.
 *
 * nginx does the same redirect in front of this (deploy/k8s/templates/web.yaml),
 * which is what a browser actually hits in production. This is the layer under
 * it, for every other way the bundle gets served - a laptop, a different proxy,
 * a URL typed after the page was already open.
 */
function ElsewhereRedirect() {
  useEffect(() => {
    window.location.replace('/login')
  }, [])
  return <FullPageLoader label="Taking you to sign in" />
}

/** Signed in as a platform administrator, or back to the portal's sign-in. */
function Guarded({ children }: { children: ReactNode }) {
  const { me, loading, servedHere } = usePlatformAuth()
  const location = useLocation()

  if (loading) return <FullPageLoader label="Loading Insuronix" />
  if (!servedHere) return <ElsewhereRedirect />
  if (!me) return <Navigate to="/admin/login" replace />
  // A temporary password is replaced before anything else: the API refuses
  // every other write until then, so sending them here makes it one step
  // rather than a wall of 403s.
  if (me.admin.mustChangePassword && location.pathname !== '/admin/password') {
    return <Navigate to="/admin/password" replace />
  }
  return <PlatformLayout title={titleFor(location.pathname)}>{children}</PlatformLayout>
}

function titleFor(path: string): string {
  if (path.startsWith('/admin/administrators')) return 'Platform administrators'
  if (path.startsWith('/admin/activity')) return 'Activity'
  if (path.startsWith('/admin/password')) return 'Your password'
  return 'Organizations'
}

function LoginOnly({ children }: { children: ReactNode }) {
  const { me, loading, servedHere } = usePlatformAuth()
  if (loading) return <FullPageLoader label="Loading Insuronix" />
  if (!servedHere) return <ElsewhereRedirect />
  if (me) return <Navigate to="/admin/organizations" replace />
  return <>{children}</>
}

function Inner() {
  return (
    <ErrorBoundary>
      <Routes>
        <Route
          path="/admin/login"
          element={
            <LoginOnly>
              <PlatformLoginPage />
            </LoginOnly>
          }
        />
        <Route
          path="/admin/password"
          element={
            <Guarded>
              <PlatformPasswordPage />
            </Guarded>
          }
        />
        <Route
          path="/admin/organizations"
          element={
            <Guarded>
              <OrganizationsPage />
            </Guarded>
          }
        />
        <Route
          path="/admin/organizations/:orgId"
          element={
            <Guarded>
              <OrganizationPage />
            </Guarded>
          }
        />
        <Route
          path="/admin/administrators"
          element={
            <Guarded>
              <AdminsPage />
            </Guarded>
          }
        />
        <Route
          path="/admin/activity"
          element={
            <Guarded>
              <AuditPage />
            </Guarded>
          }
        />
        <Route path="/admin" element={<Navigate to="/admin/organizations" replace />} />
        <Route path="*" element={<Navigate to="/admin/organizations" replace />} />
      </Routes>
    </ErrorBoundary>
  )
}

export default function PlatformApp() {
  return (
    <PlatformAuthProvider>
      <Inner />
    </PlatformAuthProvider>
  )
}
